import React, { useState, useEffect } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Button,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  Chip,
  Divider,
  CircularProgress,
  Alert,
} from '@mui/material';
import {
  Star,
  TrendingUp,
  Person,
  Reply,
} from '@mui/icons-material';
import { Link } from 'react-router-dom';
import { apiClient } from '@/utils/api-client';
import { Review } from '@/types/review-types';

interface ReviewSummaryData {
  totalReviews: number;
  averageRating: number;
  recentReviews: Review[];
  pendingResponses: number;
  ratingTrend: 'up' | 'down' | 'stable';
}

interface ReviewSummaryWidgetProps {
  maxReviews?: number;
}

export const ReviewSummaryWidget: React.FC<ReviewSummaryWidgetProps> = ({
  maxReviews = 3,
}) => {
  const [data, setData] = useState<ReviewSummaryData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadReviewSummary();
  }, []);

  const loadReviewSummary = async () => {
    setLoading(true);
    setError(null);

    try {
      // In a real implementation, this would be a dedicated endpoint
      // For now, we'll simulate the data
      const mockData: ReviewSummaryData = {
        totalReviews: 47,
        averageRating: 4.6,
        recentReviews: [
          {
            reviewId: 'rev_1',
            bookingId: 'book_1',
            reviewerType: 'guest',
            reviewerId: 'guest_1',
            revieweeId: 'host_1',
            listingId: 'listing_1',
            overallRating: 5,
            categoryRatings: {
              cleanliness: 5,
              communication: 5,
              location: 4,
            },
            reviewText: 'Amazing stay! The host was very responsive and the place was spotless.',
            reviewerName: 'Sarah M.',
            reviewerPhotoUrl: undefined,
            isPublic: true,
            status: 'active',
            createdAt: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000).toISOString(),
            updatedAt: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000).toISOString(),
            reviewDeadline: new Date(Date.now() + 28 * 24 * 60 * 60 * 1000).toISOString(),
          },
          {
            reviewId: 'rev_2',
            bookingId: 'book_2',
            reviewerType: 'guest',
            reviewerId: 'guest_2',
            revieweeId: 'host_1',
            listingId: 'listing_2',
            overallRating: 4,
            categoryRatings: {
              cleanliness: 4,
              communication: 5,
              location: 4,
            },
            reviewText: 'Great location and very clean. Would definitely stay again!',
            reviewerName: 'Mike R.',
            reviewerPhotoUrl: undefined,
            isPublic: true,
            status: 'active',
            createdAt: new Date(Date.now() - 5 * 24 * 60 * 60 * 1000).toISOString(),
            updatedAt: new Date(Date.now() - 5 * 24 * 60 * 60 * 1000).toISOString(),
            reviewDeadline: new Date(Date.now() + 25 * 24 * 60 * 60 * 1000).toISOString(),
          },
          {
            reviewId: 'rev_3',
            bookingId: 'book_3',
            reviewerType: 'guest',
            reviewerId: 'guest_3',
            revieweeId: 'host_1',
            listingId: 'listing_1',
            overallRating: 5,
            categoryRatings: {
              cleanliness: 5,
              communication: 4,
              location: 5,
            },
            reviewText: 'Perfect for our weekend getaway. Highly recommend!',
            reviewerName: 'Emma L.',
            reviewerPhotoUrl: undefined,
            isPublic: true,
            status: 'active',
            createdAt: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString(),
            updatedAt: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString(),
            reviewDeadline: new Date(Date.now() + 23 * 24 * 60 * 60 * 1000).toISOString(),
          },
        ],
        pendingResponses: 2,
        ratingTrend: 'up',
      };

      setData(mockData);
    } catch (err) {
      console.error('Error loading review summary:', err);
      setError(err instanceof Error ? err.message : 'Failed to load review summary');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    const now = new Date();
    const diffTime = Math.abs(now.getTime() - date.getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays} days ago`;
    if (diffDays < 30) return `${Math.ceil(diffDays / 7)} weeks ago`;
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const renderStars = (rating: number) => {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        {[1, 2, 3, 4, 5].map((star) => (
          <Star
            key={star}
            sx={{
              fontSize: 16,
              color: star <= rating ? 'warning.main' : 'grey.300',
            }}
          />
        ))}
        <Typography variant="body2" sx={{ ml: 0.5, fontWeight: 'medium' }}>
          {rating.toFixed(1)}
        </Typography>
      </Box>
    );
  };

  if (loading) {
    return (
      <Card sx={{ height: 'fit-content' }}>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 4 }}>
            <CircularProgress size={24} />
            <Typography variant="body2" sx={{ ml: 2 }}>
              Loading reviews...
            </Typography>
          </Box>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card sx={{ height: 'fit-content' }}>
        <CardContent>
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
          <Button onClick={loadReviewSummary} size="small">
            Retry
          </Button>
        </CardContent>
      </Card>
    );
  }

  if (!data) {
    return null;
  }

  return (
    <Card sx={{ height: 'fit-content' }}>
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
            Reviews & Ratings
          </Typography>
          <Button
            component={Link}
            to="/reviews"
            size="small"
            endIcon={<TrendingUp />}
          >
            View All
          </Button>
        </Box>

        {/* Rating Summary */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3, p: 2, bgcolor: 'grey.50', borderRadius: 2 }}>
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="h4" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
              {data.averageRating.toFixed(1)}
            </Typography>
            {renderStars(data.averageRating)}
          </Box>
          <Divider orientation="vertical" flexItem />
          <Box sx={{ flex: 1 }}>
            <Typography variant="body2" color="text.secondary">
              {data.totalReviews} total reviews
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
              <TrendingUp 
                sx={{ 
                  fontSize: 16, 
                  color: data.ratingTrend === 'up' ? 'success.main' : 
                         data.ratingTrend === 'down' ? 'error.main' : 'grey.500' 
                }} 
              />
              <Typography variant="caption" color="text.secondary">
                {data.ratingTrend === 'up' ? 'Trending up' : 
                 data.ratingTrend === 'down' ? 'Trending down' : 'Stable'}
              </Typography>
            </Box>
          </Box>
        </Box>

        {/* Pending Responses Alert */}
        {data.pendingResponses > 0 && (
          <Alert 
            severity="info" 
            sx={{ mb: 3 }}
            action={
              <Button size="small" startIcon={<Reply />}>
                Respond
              </Button>
            }
          >
            You have {data.pendingResponses} review{data.pendingResponses !== 1 ? 's' : ''} waiting for your response
          </Alert>
        )}

        {/* Recent Reviews */}
        {data.recentReviews.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Star sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h6" color="text.secondary" gutterBottom>
              No reviews yet
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Reviews from your guests will appear here
            </Typography>
          </Box>
        ) : (
          <List sx={{ p: 0 }}>
            {data.recentReviews.slice(0, maxReviews).map((review, index) => (
              <React.Fragment key={review.reviewId}>
                <ListItem sx={{ px: 0, alignItems: 'flex-start' }}>
                  <ListItemAvatar>
                    <Avatar sx={{ bgcolor: 'primary.main' }}>
                      <Person />
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                          {review.reviewerName}
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          {renderStars(review.overallRating)}
                          <Typography variant="caption" color="text.secondary">
                            {formatDate(review.createdAt)}
                          </Typography>
                        </Box>
                      </Box>
                    }
                    secondary={
                      <Box>
                        <Typography 
                          variant="body2" 
                          color="text.secondary"
                          sx={{ 
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden',
                            mb: 1
                          }}
                        >
                          {review.reviewText}
                        </Typography>
                        {!review.response && (
                          <Chip
                            label="Needs Response"
                            size="small"
                            color="warning"
                            variant="outlined"
                            sx={{ fontSize: '0.7rem', height: 20 }}
                          />
                        )}
                      </Box>
                    }
                  />
                </ListItem>
                {index < Math.min(data.recentReviews.length, maxReviews) - 1 && <Divider />}
              </React.Fragment>
            ))}
          </List>
        )}

        {/* Quick Actions */}
        <Box sx={{ mt: 3, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 2 }}>
            Quick Actions
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <Button
              size="small"
              variant="outlined"
              component={Link}
              to="/reviews"
              startIcon={<Star />}
            >
              Manage Reviews
            </Button>
            {data.pendingResponses > 0 && (
              <Button
                size="small"
                variant="contained"
                component={Link}
                to="/reviews?filter=pending-response"
                startIcon={<Reply />}
              >
                Respond ({data.pendingResponses})
              </Button>
            )}
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};

export default ReviewSummaryWidget;